import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Event as EventIcon,
  Assignment as AssignmentIcon,
  Announcement as AnnouncementIcon,
  VideoCall as VideoCallIcon,
  Quiz as QuizIcon,
  Receipt as ReceiptIcon,
  Photo as PhotoIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Payment as PaymentIcon,
  Cake as CakeIcon,
  SwapHoriz as SwapHorizIcon,
  Close as CloseIcon,
  Videocam as VideocamIcon,
  Message as MessageIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Menu as MenuIcon,
  MenuBook as LessonPlanIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';

const menuItems = [
  { label: 'Notice', icon: <AnnouncementIcon />, path: '/notices', color: '#0066CC' },
  { label: 'Circular', icon: <DescriptionIcon />, path: '/circulars', color: '#00ACC1' },
  { label: 'Assignment', icon: <AssignmentIcon />, path: '/assignments', color: '#43A047' },
  { label: 'Lesson Plans', icon: <LessonPlanIcon />, path: '/lesson-plans', color: '#7B1FA2' },
  { label: 'My Attendance', icon: <EventIcon />, path: '/attendance', color: '#FB8C00' },
  { label: 'My Bills', icon: <ReceiptIcon />, path: '/bills', color: '#8E24AA' },
  { label: 'My Receipts', icon: <ReceiptIcon />, path: '/receipts', color: '#546E7A' },
  { label: 'Online Payment', icon: <PaymentIcon />, path: '/online-payment', color: '#1E88E5' },
  { label: 'Birthday', icon: <CakeIcon />, path: '/birthday', color: '#FF7043' },
  { label: 'My Teachers', icon: <PeopleIcon />, path: '/teachers', color: '#6D4C41' },
  { label: 'Online Exam', icon: <QuizIcon />, path: '/online-exams', color: '#5C6BC0' },
  { label: 'Online Class', icon: <VideoCallIcon />, path: '/online-classes', color: '#26C6DA' },
  { label: 'Photo Album', icon: <PhotoIcon />, path: '/gallery', color: '#EC407A' },
  { label: 'Video Gallery', icon: <VideocamIcon />, path: '/videos', color: '#66BB6A' },
];

const HomeScreen = () => {
  const navigate = useNavigate();
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [studentInfo, setStudentInfo] = useState(null);
  const [branchFyInfo, setBranchFyInfo] = useState(null);
  const [fyYearList, setFyYearList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFyPicker, setShowFyPicker] = useState(false);
  const [showBranchPicker, setShowBranchPicker] = useState(false);
  const [changingFy, setChangingFy] = useState(false);
  const [changingBranch, setChangingBranch] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    await Promise.all([
      loadSchoolInfo(),
      loadStudentInfo(),
      loadBranchFyInfo()
    ]);
  };

  const loadSchoolInfo = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/token/get-school-info', {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
     // console.log('School Info:', response.data);
      setSchoolInfo(response.data);
    } catch (error) {
      console.error('HomeScreen loadSchoolInfo error:', error);
      handleCatch(error);
    }
  };

  const loadStudentInfo = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/master/student/get-student-profile', {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
     // console.log('Student Info:', response.data);
      setStudentInfo(response.data);
    } catch (error) {
      console.error('HomeScreen loadStudentInfo error:', error);
      handleCatch(error);
    }
  };

  const loadBranchFyInfo = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/student-login/get-my-branch-fy-name', {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      setBranchFyInfo(response.data);
    } catch (error) {
      console.error('HomeScreen loadBranchFyInfo error:', error);
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const loadFyYearList = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/student-login/get-fy-year-list', {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      setFyYearList(response.data);
    } catch (error) {
      console.error('HomeScreen loadFyYearList error:', error);
      handleCatch(error);
    }
  };

  const loadBranchList = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/student-login/get-branch-list', {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      setBranchList(response.data);
    } catch (error) {
      console.error('HomeScreen loadBranchList error:', error);
      handleCatch(error);
    }
  };

  const handleChangeFyPress = async () => {
    if (fyYearList.length === 0) {
      await loadFyYearList();
    }
    setShowFyPicker(true);
  };

  const handleChangeBranchPress = async () => {
    if (branchList.length === 0) {
      await loadBranchList();
    }
    setShowBranchPicker(true);
  };

  const changeFyYear = async (fyId) => {
    try {
      setChangingFy(true);
      const token = localStorage.getItem('userToken');
      await api.post('api/student-login/change-fy', { FyId: fyId }, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      
      await loadBranchFyInfo();
      setShowFyPicker(false);
      alert('Financial year changed successfully!');
    } catch (error) {
      console.error('HomeScreen changeFyYear error:', error);
      handleCatch(error);
      alert('Failed to change financial year. Please try again.');
    } finally {
      setChangingFy(false);
    }
  };

  const changeBranch = async (branchId) => {
    try {
      setChangingBranch(true);
      const token = localStorage.getItem('userToken');
      await api.post('api/student-login/change-branch', branchId, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      
      await loadBranchFyInfo();
      setShowBranchPicker(false);
      alert('Branch changed successfully!');
    } catch (error) {
      console.error('HomeScreen changeBranch error:', error);
      handleCatch(error);
      alert('Failed to change branch. Please try again.');
    } finally {
      setChangingBranch(false);
    }
  };

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', backgroundColor: '#F5F7FA' }}>

      {/* ── Gradient student header ─────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.BLUE} 0%, #0D47A1 100%)`,
          px: 2,
          pt: 1.5,
          pb: 1.5,
          flexShrink: 0,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', mr: 1.5, flexShrink: 0 }} />
            <Box>
              <Box sx={{ width: 120, height: 14, bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 1, mb: 0.7 }} />
              <Box sx={{ width: 80, height: 11, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1 }} />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              src={studentInfo?.photoUrl}
              sx={{
                width: 44,
                height: 44,
                mr: 1.5,
                border: '2px solid rgba(255,255,255,0.7)',
                flexShrink: 0,
              }}
            >
              {studentInfo?.studentName?.charAt(0)?.toUpperCase() || 'S'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '15px',
                  color: '#fff',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {studentInfo?.studentName || 'Student'}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', lineHeight: 1.3, mt: 0.2, fontWeight: 500 }}>
                {studentInfo?.class
                  ? `${studentInfo.class}${studentInfo.section ? ' - ' + studentInfo.section : ''}${studentInfo.rollNo ? '  •  Roll ' + studentInfo.rollNo : ''}`
                  : ''}
              </Typography>
              {schoolInfo?.schoolName && (
                <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {schoolInfo.schoolName}
                </Typography>
              )}
            </Box>
            {/* Hamburger — opens NativeDrawer */}
            <IconButton
              onClick={() => window.NativeBridge?.openDrawer()}
              sx={{ color: '#fff', ml: 1, p: '6px', flexShrink: 0 }}
              aria-label="menu"
            >
              <MenuIcon sx={{ fontSize: 26 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* ── Branch / FY chips row ────────────────────────────────── */}
      {branchFyInfo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.8,
            backgroundColor: '#fff',
            borderBottom: '1px solid #e8ecf0',
            overflowX: 'auto',
            flexShrink: 0,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {/* Branch chip */}
          <Box
            onClick={handleChangeBranchPress}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.2,
              py: 0.5,
              borderRadius: 20,
              border: `1px solid ${colors.BLUE}`,
              cursor: 'pointer',
              flexShrink: 0,
              WebkitTapHighlightColor: 'transparent',
              '&:active': { backgroundColor: '#e8f0fe' },
            }}
          >
            <Typography sx={{ fontSize: '11px', color: colors.BLUE, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {branchFyInfo.branchName || 'Branch'}
            </Typography>
            <ArrowDownIcon sx={{ fontSize: 14, color: colors.BLUE }} />
          </Box>

          {/* FY chip */}
          <Box
            onClick={handleChangeFyPress}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.2,
              py: 0.5,
              borderRadius: 20,
              border: `1px solid #FB8C00`,
              cursor: 'pointer',
              flexShrink: 0,
              WebkitTapHighlightColor: 'transparent',
              '&:active': { backgroundColor: '#fff3e0' },
            }}
          >
            <EventIcon sx={{ fontSize: 12, color: '#FB8C00' }} />
            <Typography sx={{ fontSize: '11px', color: '#FB8C00', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {branchFyInfo.fyStartDate
                ? `${formatDate(branchFyInfo.fyStartDate)} – ${formatDate(branchFyInfo.fyEndDate)}`
                : 'Financial Year'}
            </Typography>
            <ArrowDownIcon sx={{ fontSize: 14, color: '#FB8C00' }} />
          </Box>
        </Box>
      )}

      {/* ── School name strip ────────────────────────────────────── */}
      {schoolInfo?.schoolName && (
        <Box sx={{ px: 2, py: 0.6, backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: '12px',
              color: colors.BLUE,
              fontWeight: 600,
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {schoolInfo.schoolName}
          </Typography>
        </Box>
      )}

      {/* ── 3-column menu grid ───────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          px: 1.5,
          pt: 1.5,
          pb: 2,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1.2,
          }}
        >
          {menuItems.map((item) => (
            <Box
              key={item.label}
              onClick={() => handleMenuClick(item.path)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fff',
                borderRadius: '14px',
                py: 2,
                px: 0.5,
                cursor: 'pointer',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                WebkitTapHighlightColor: 'transparent',
                '&:active': {
                  transform: 'scale(0.95)',
                  backgroundColor: '#f0f4ff',
                },
                transition: 'transform 0.1s ease',
              }}
            >
              {/* Icon circle */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: `${item.color}1A`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 0.8,
                  color: item.color,
                  '& svg': { fontSize: 26 },
                }}
              >
                {item.icon}
              </Box>

              {/* Label */}
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#333',
                  textAlign: 'center',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  px: 0.3,
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── FY Picker Dialog ─────────────────────────────────────── */}
      <Dialog
        open={showFyPicker}
        onClose={() => setShowFyPicker(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Select Financial Year</Typography>
          <IconButton onClick={() => setShowFyPicker(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List>
            {fyYearList.map((item, index) => (
              <React.Fragment key={item.no}>
                <ListItem
                  button
                  onClick={() => changeFyYear(item.no)}
                  disabled={changingFy}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <ListItemText
                    primary={<Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{item.companyName}</Typography>}
                    secondary={`${formatDate(item.date1)} – ${formatDate(item.date2)}`}
                  />
                  {changingFy && <CircularProgress size={18} />}
                </ListItem>
                {index < fyYearList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* ── Branch Picker Dialog ─────────────────────────────────── */}
      <Dialog
        open={showBranchPicker}
        onClose={() => setShowBranchPicker(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Select Branch</Typography>
          <IconButton onClick={() => setShowBranchPicker(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List>
            {branchList.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem
                  button
                  onClick={() => changeBranch(item.id)}
                  disabled={changingBranch}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <ListItemText
                    primary={<Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{item.branchName}</Typography>}
                    secondary={item.address}
                  />
                  {changingBranch && <CircularProgress size={18} />}
                </ListItem>
                {index < branchList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default HomeScreen;
